import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Layers, Hash, Loader2, CheckCircle, ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import { useCreateCategory, useUpdateCategory } from '../../services/adminService';
import { slugify } from '../../lib/utils';
import {
  ICON_PICKER_OPTIONS,
  getCategoryIcon,
  iconLabel,
} from '../../lib/categoryIcons';

const overlay = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modal = {
  hidden: { opacity: 0, scale: 0.95, y: 10 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { type: 'spring', damping: 25, stiffness: 300 } },
  exit: { opacity: 0, scale: 0.95, y: 10, transition: { duration: 0.15 } },
};

/**
 * Modal for creating or editing a category.
 * @param {{ category: object|null, open: boolean, onClose: () => void }} props
 * category = null → create mode; category = {...} → edit mode.
 */
export function CategoryFormModal({ category, open, onClose }) {
  const createMutation = useCreateCategory();
  const updateMutation = useUpdateCategory();
  const isEdit = !!category;

  const [form, setForm] = useState({ name: '', slug: '', icon: '' });
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setSuccess(false);
      if (category) {
        setForm({
          name: category.name || '',
          slug: category.slug || '',
          icon: category.icon || '',
        });
      } else {
        setForm({ name: '', slug: '', icon: '' });
      }
    }
  }, [category, open]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleNameChange = (val) => {
    update('name', val);
    if (!isEdit) {
      update('slug', slugify(val));
    }
  };

  const handleKeyDown = useCallback(
    (e) => { if (e.key === 'Escape') onClose(); },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const mutation = isEdit ? updateMutation : createMutation;

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccess(false);

    if (isEdit) {
      updateMutation.mutate(
        { id: category.id, updates: { name: form.name, slug: form.slug, icon: form.icon || null } },
        { onSuccess: () => { setSuccess(true); setTimeout(onClose, 800); } }
      );
    } else {
      createMutation.mutate(
        { name: form.name, slug: form.slug, icon: form.icon || null },
        { onSuccess: () => { setSuccess(true); setTimeout(onClose, 800); } }
      );
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="cat-modal-backdrop"
            variants={overlay}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
            className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-sm"
          />

          <motion.div
            key="cat-modal"
            variants={modal}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
          >
            <div className="bg-brand-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-ui-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <Layers size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-brand-dark">
                      {isEdit ? 'Edit Category' : 'Add Category'}
                    </h3>
                    <p className="text-xs text-brand-gray">
                      {isEdit ? `Update "${category.name}"` : 'Create a new menu category'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg text-brand-gray hover:bg-brand-light hover:text-brand-dark transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                    Category Name
                  </label>
                  <div className="relative">
                    <Layers size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      placeholder="e.g. Burgers"
                      autoFocus
                      className="w-full pl-10 pr-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                    />
                  </div>
                </div>

                {/* Slug */}
                <div>
                  <label className="block text-sm font-semibold text-brand-dark mb-1.5">Slug</label>
                  <div className="relative">
                    <Hash size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
                    <input
                      type="text"
                      required
                      value={form.slug}
                      onChange={(e) => update('slug', e.target.value)}
                      placeholder="burgers"
                      className="w-full pl-10 pr-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                    />
                  </div>
                </div>

                {/* Icon picker */}
                <div>
                  <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                    Icon
                  </label>

                  {/* Selected icon preview */}
                  {form.icon && (() => {
                    const SelectedIcon = getCategoryIcon(form.icon);
                    return (
                      <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-brand-light/50 border border-ui-border rounded-xl">
                        <SelectedIcon size={18} className="text-brand-red" />
                        <span className="text-sm font-bold text-brand-dark">{iconLabel(form.icon)}</span>
                        <button
                          type="button"
                          onClick={() => update('icon', '')}
                          className="ml-auto p-1 rounded hover:bg-brand-light"
                        >
                          <X size={12} className="text-brand-gray" />
                        </button>
                      </div>
                    );
                  })()}

                  {/* Icon grid */}
                  <div className="max-h-48 overflow-y-auto border border-ui-border rounded-xl p-3 space-y-3 bg-brand-light/30">
                    {ICON_PICKER_OPTIONS.map(({ group, icons }) => (
                      <div key={group}>
                        <p className="text-[10px] font-bold text-brand-gray uppercase tracking-wide mb-1.5">{group}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {icons.map((name) => {
                            const Icon = getCategoryIcon(name);
                            const isSelected = form.icon === name;
                            return (
                              <button
                                key={name}
                                type="button"
                                onClick={() => update('icon', name)}
                                title={iconLabel(name)}
                                className={clsx(
                                  'w-9 h-9 rounded-lg flex items-center justify-center transition-all',
                                  isSelected
                                    ? 'bg-brand-red text-brand-white ring-2 ring-brand-red/30 shadow-sm'
                                    : 'bg-brand-white border border-ui-border text-brand-gray hover:bg-brand-light hover:text-brand-dark'
                                )}
                              >
                                <Icon size={16} />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </form>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-ui-border flex items-center gap-3 bg-ui-background">
                {success ? (
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="flex items-center gap-2 text-success font-bold text-sm"
                  >
                    <CheckCircle size={18} />
                    {isEdit ? 'Updated!' : 'Created!'}
                  </motion.div>
                ) : (
                  <>
                    <button
                      type="submit"
                      onClick={(e) => {
                        e.currentTarget
                          .closest('.bg-brand-white')
                          .querySelector('form')
                          .requestSubmit();
                      }}
                      disabled={mutation.isPending}
                      className={clsx(
                        'flex items-center gap-2 px-5 py-2.5 text-sm font-bold rounded-xl transition-all',
                        mutation.isPending
                          ? 'bg-brand-red/60 text-brand-white cursor-not-allowed'
                          : 'bg-brand-red hover:bg-red-700 text-brand-white shadow-lg'
                      )}
                    >
                      {mutation.isPending && <Loader2 size={14} className="animate-spin" />}
                      {mutation.isPending
                        ? 'Saving...'
                        : isEdit
                          ? 'Save Changes'
                          : 'Create Category'}
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-5 py-2.5 text-sm font-bold text-brand-gray bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
                    >
                      Cancel
                    </button>
                  </>
                )}

                {mutation.isError && (
                  <p className="text-xs text-error ml-auto">
                    {mutation.error?.message?.includes('categories_name_unique')
                      ? 'A category with this name already exists'
                      : mutation.error?.message || 'Failed to save'}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
